<template>
  <HrPageLayout
    title="Public Service Board — Overview"
    subtitle="Recruitment, appointments, probation and board operations at a glance"
    icon="🏛️"
    :breadcrumb-items="[
      { label: 'Home', route: '/dashboard' },
      { label: 'Public Service Board' },
      { label: 'Overview' }
    ]"
  >
    <template #header-actions>
      <button type="button" class="hr-btn hr-btn-outline-primary" :disabled="loading" @click="loadAll">
        <i class="bx bx-refresh" :class="{ 'bx-spin': loading }"></i> Refresh
      </button>
    </template>

    <div class="psb-wrap">
      <div v-if="error" class="hr-alert hr-alert-danger mb-3">
        {{ error }}
        <button type="button" class="hr-btn hr-btn-sm mt-2" @click="loadAll">Retry</button>
      </div>

      <div class="metrics">
        <div class="metric m1">
          <span>Active vacancies</span>
          <strong>{{ stats.activeVacancies }}</strong>
        </div>
        <div class="metric m2">
          <span>Closed vacancies</span>
          <strong>{{ stats.closedVacancies }}</strong>
        </div>
        <div class="metric m3">
          <span>Applicants (listed)</span>
          <strong>{{ stats.applicants }}</strong>
        </div>
        <div class="metric m4">
          <span>On probation</span>
          <strong>{{ stats.onProbation }}</strong>
        </div>
        <div class="metric m5">
          <span>Probation alerts</span>
          <strong>{{ stats.probationAlerts }}</strong>
        </div>
        <div class="metric m6">
          <span>Short-term contracts</span>
          <strong>{{ stats.shortTerm }}</strong>
        </div>
      </div>

      <div class="grid-2">
        <div class="panel">
          <div class="panel-h">
            <h4>Quick links</h4>
          </div>
          <div class="links">
            <router-link v-for="l in links" :key="l.route" :to="{ name: l.route }" class="link-card">
              <i :class="l.icon"></i>
              <span>{{ l.title }}</span>
            </router-link>
          </div>
        </div>

        <div class="panel">
          <div class="panel-h">
            <h4>Recent vacancies</h4>
            <router-link :to="{ name: 'vacancy-management' }">View all</router-link>
          </div>
          <div v-if="loading && !recentVacancies.length" class="muted">Loading…</div>
          <table v-else class="tbl">
            <thead>
              <tr><th>Title</th><th>Status</th><th>Applicants</th></tr>
            </thead>
            <tbody>
              <tr v-for="v in recentVacancies" :key="v.id">
                <td>{{ v.title }}</td>
                <td><span class="badge" :class="String(v.status).toLowerCase() === 'active' ? 'ok' : ''">{{ v.status }}</span></td>
                <td>{{ v.applicants_count ?? 0 }}</td>
              </tr>
              <tr v-if="!recentVacancies.length">
                <td colspan="3" class="muted">No vacancies loaded.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="panel">
        <div class="panel-h">
          <h4>Probation spotlight</h4>
          <router-link :to="{ name: 'module-confirmation-probation' }">Open module</router-link>
        </div>
        <table class="tbl">
          <thead>
            <tr><th>Employee</th><th>Department</th><th>End date</th><th>Status</th></tr>
          </thead>
          <tbody>
            <tr v-for="p in recentProbation" :key="'p' + p.employee_id">
              <td>{{ p.employee_name }}</td>
              <td>{{ p.departmentName || '—' }}</td>
              <td>{{ p.probation_end || '—' }}</td>
              <td>{{ p.status }}</td>
            </tr>
            <tr v-if="!recentProbation.length">
              <td colspan="4" class="muted">No probation records in spotlight.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute } from "@/api";

export default {
  name: "PublicServiceDashboard",
  components: { HrPageLayout },
  data() {
    return {
      loading: false,
      error: "",
      stats: {
        activeVacancies: 0,
        closedVacancies: 0,
        applicants: 0,
        onProbation: 0,
        probationAlerts: 0,
        shortTerm: 0
      },
      recentVacancies: [],
      recentProbation: [],
      links: [
        { title: "Vacancy Management", route: "vacancy-management", icon: "bx bx-briefcase" },
        { title: "Applicants", route: "applicants", icon: "bx bx-group" },
        { title: "Shortlisting", route: "shortlisting-management", icon: "bx bx-user-check" },
        { title: "Interviews", route: "interview-management", icon: "bx bx-message-detail" },
        { title: "Appointments", route: "appointment-management", icon: "bx bx-file" },
        { title: "Confirmation & Probation", route: "module-confirmation-probation", icon: "bx bx-check-shield" },
        { title: "Short Term Employment", route: "module-short-term-employment", icon: "bx bx-time-five" },
        { title: "Board Decisions", route: "module-board-decisions", icon: "bx bx-gavel" },
        { title: "Staff Establishment", route: "staff-establishment", icon: "bx bx-sitemap" }
      ]
    };
  },
  mounted() {
    this.loadAll();
  },
  methods: {
    async post(fn, fields = {}) {
      const fd = new FormData();
      fd.append("function", fn);
      Object.keys(fields).forEach((k) => fd.append(k, fields[k] == null ? "" : String(fields[k])));
      return execute(fd, authUrl);
    },
    async loadAll() {
      this.loading = true;
      this.error = "";
      try {
        const [vacRes, probRes, steRes] = await Promise.all([
          this.post("getVacancies", { page: 1, pageSize: 8, search: "", status: "" }),
          this.post("getProbationEmployeesDashboard"),
          this.post("listShortTermEmploymentContracts", { year: String(new Date().getFullYear()) })
        ]);

        const vacPayload = vacRes.data?.success ? vacRes.data.data : null;
        const vacList = vacPayload?.vacancies || vacPayload?.data || [];
        this.recentVacancies = Array.isArray(vacList) ? vacList.slice(0, 8) : [];
        const totalV = Number(vacPayload?.pagination?.total || vacList.length || 0);
        let active = 0;
        let closed = 0;
        let applicants = 0;
        this.recentVacancies.forEach((v) => {
          const st = String(v.status || "").toLowerCase();
          if (st === "active") active += 1;
          else closed += 1;
          applicants += Number(v.applicants_count || 0);
        });
        // Prefer status filter totals when API only returns current page
        try {
          const [aRes, cRes] = await Promise.all([
            this.post("getVacancies", { page: 1, pageSize: 1, status: "active" }),
            this.post("getVacancies", { page: 1, pageSize: 1, status: "closed" })
          ]);
          active = Number(aRes.data?.data?.pagination?.total ?? active);
          closed = Number(cRes.data?.data?.pagination?.total ?? closed);
        } catch (_) { /* keep page counts */ }

        this.stats.activeVacancies = active;
        this.stats.closedVacancies = closed;
        this.stats.applicants = applicants || totalV;

        const probRows = probRes.data?.success ? (probRes.data.data?.data || []) : [];
        const alerts = probRes.data?.success ? (probRes.data.data?.alerts || []) : [];
        this.stats.onProbation = Array.isArray(probRows) ? probRows.filter((r) => String(r.status || "").toLowerCase().includes("probation")).length || probRows.length : 0;
        this.stats.probationAlerts = Array.isArray(alerts) ? alerts.length : 0;
        this.recentProbation = (Array.isArray(probRows) ? probRows : [])
          .filter((r) => r.alert_one_month || Number(r.remaining_days || 999) <= 30)
          .slice(0, 8);
        if (!this.recentProbation.length && Array.isArray(probRows)) {
          this.recentProbation = probRows.slice(0, 5);
        }

        const steRows = steRes.data?.success ? (steRes.data.data?.rows || []) : [];
        this.stats.shortTerm = Array.isArray(steRows) ? steRows.length : 0;
      } catch (e) {
        this.error = "Could not load Public Service overview.";
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.psb-wrap { display: flex; flex-direction: column; gap: 16px; }
.metrics {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
}
.metric {
  border-radius: 14px;
  padding: 16px;
  color: #fff;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
}
.metric span { display: block; font-size: 12px; opacity: 0.9; margin-bottom: 6px; }
.metric strong { font-size: 1.6rem; }
.m1 { background: linear-gradient(135deg, #0c2d48, #164e63); }
.m2 { background: linear-gradient(135deg, #334155, #475569); }
.m3 { background: linear-gradient(135deg, #0e7490, #0891b2); }
.m4 { background: linear-gradient(135deg, #166534, #15803d); }
.m5 { background: linear-gradient(135deg, #b45309, #d97706); }
.m6 { background: linear-gradient(135deg, #1e3a5f, #2563eb); }
.grid-2 {
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: 14px;
}
@media (max-width: 900px) {
  .grid-2 { grid-template-columns: 1fr; }
}
.panel {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 14px 16px;
}
.panel-h {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.panel-h h4 { margin: 0; color: #0c2d48; }
.panel-h a { font-size: 13px; color: #0e7490; text-decoration: none; font-weight: 600; }
.links {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}
.link-card {
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 10px 12px;
  border-radius: 10px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  color: #0f172a;
  text-decoration: none;
  font-size: 13px;
  font-weight: 600;
}
.link-card:hover { border-color: #0e7490; background: #ecfeff; }
.link-card i { color: #0e7490; font-size: 18px; }
.tbl { width: 100%; border-collapse: collapse; font-size: 13px; }
.tbl th, .tbl td { padding: 8px 6px; border-bottom: 1px solid #f1f5f9; text-align: left; }
.tbl th { color: #64748b; font-weight: 600; font-size: 12px; }
.badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  background: #e2e8f0;
  font-size: 11px;
  font-weight: 700;
}
.badge.ok { background: #dcfce7; color: #166534; }
.muted { color: #94a3b8; text-align: center; padding: 12px; }
.mb-3 { margin-bottom: 12px; }
.mt-2 { margin-top: 8px; }
</style>
